import { useMemo, useState } from 'react';
import classnames from 'classnames';
import { Tooltip } from '@ds';
import { MapFilterType } from '@lp/features/RoutePlanner/types';
import { MARKER_TYPE_ICONS } from '@lp/features/RoutePlanner/config';
import { ChipWithStatus } from '@ui';
import { OcsConsoleData, ResourceStatus } from '@/api/apis/resources';
import { ocsConsoleStatusName } from '@/constants/resources.constants';

type HubDevicesTooltipProps = {
  list: OcsConsoleData[];
  title: string;
};

export const HubDevicesTooltip = ({ list, title }: HubDevicesTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const reservedListNumber = useMemo(() => {
    return list.filter((i) =>
      i.reservations.some((r) => ![ResourceStatus.Done, ResourceStatus.Canceled].includes(r.status)),
    ).length;
  }, [list]);

  return (
    <Tooltip
      placement="bottom"
      onOpenChange={(value) => setIsOpen(value)}
      showArrow
      closeDelay={0}
      classNames={{
        base: 'group',
        content: 'shadow-xl opacity-90',
      }}
      content={
        <div className="py-2 flex flex-col gap-2">
          <div className="flex gap-1">
            <span className="text-m-sb text-black">{title}</span>
            <span className="text-m-r text-black">(OCS Consoles)</span>
          </div>
          {list.map((i, index) => (
            <div
              key={i.id}
              className={classnames('flex gap-2 items-center py-1 px-2 rounded-lg', {
                'bg-base-80': index % 2,
                'bg-white': !(index % 2),
                'opacity-70': i.reservations.some(
                  (r) => ![ResourceStatus.Done, ResourceStatus.Canceled].includes(r.status),
                ),
              })}
            >
              {MARKER_TYPE_ICONS[MapFilterType.ocsDevices]}
              <span className="text-s-sb text-black">{i.fullSerialNumber}</span>
              {!!i.status && (
                <ChipWithStatus size="xs" status={i.status}>
                  {ocsConsoleStatusName[i.status]}
                </ChipWithStatus>
              )}
              <span className="text-s-r text-black">{i.lastInUseDaysAgo}d</span>
            </div>
          ))}
        </div>
      }
    >
      <div
        className={`absolute -bottom-9 w-max rounded-3xl py-1.5 px-2 flex gap-2 items-center text-base-5 shadow-xl hover:bg-base-60 ${isOpen ? 'bg-base-60' : 'bg-white'}`}
      >
        {MARKER_TYPE_ICONS[MapFilterType.ocsDevices]}
        <span className="text-s-sb text-base-5">{list.length - reservedListNumber}</span>
        {!!reservedListNumber && (
          <span className="block bg-base-70 px-1 text-s-sb text-base-30 rounded-md">{reservedListNumber}</span>
        )}
      </div>
    </Tooltip>
  );
};
